import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Button, Skeleton, Spin, message } from "antd";
import {
  addAttractionToOrder,
  calculateAttractionPrice,
  searchAttractions,
} from "../../services/attractionsApi";
import { attractionShowsInBillableUpsell } from "../../utils/packageAttractionRulesDisplay";
import "./AttractionsUpsellBlock.scss";

function ldsYyyymmddToIso(v) {
  if (v == null) return null;
  const s = String(v).replace(/\D/g, "");
  if (s.length < 8) return null;
  return `${s.slice(0, 4)}-${s.slice(4, 6)}-${s.slice(6, 8)}`;
}

export function parseDdMmYyyyToIso(s) {
  if (!s || typeof s !== "string") return null;
  const parts = s.split("/");
  if (parts.length !== 3) return null;
  const [dd, mm, yyyy] = parts;
  if (!dd || !mm || !yyyy) return null;
  return `${yyyy}-${String(mm).padStart(2, "0")}-${String(dd).padStart(2, "0")}`;
}

/** תאריך ביקור מ-searchPhrase (checkIn או depDate). */
export function resolveVisitIsoFromSearchPhrase(sp) {
  if (!sp || typeof sp !== "object") return null;
  const ci = sp.checkIn;
  if (ci && /^\d{4}-\d{2}-\d{2}/.test(String(ci))) return String(ci).slice(0, 10);
  const dep = sp.depDate;
  if (dep && String(dep).includes("/")) return parseDdMmYyyyToIso(dep);
  return null;
}

export function countPaxFromSearchPhrase(sp) {
  if (!sp?.rooms || !Array.isArray(sp.rooms)) return 2;
  let n = 0;
  sp.rooms.forEach((r) => {
    n += Number(r.adults || 0) + Number(r.children || r.kids || 0);
  });
  return Math.max(1, n || 2);
}

function countPaxFromRooms(roomsAndPaxInfo) {
  if (!Array.isArray(roomsAndPaxInfo)) return 1;
  let n = 0;
  roomsAndPaxInfo.forEach((room) => {
    n += (room?.adults?.length || 0) + (room?.kids?.length || 0);
  });
  return Math.max(1, n || 1);
}

/**
 * אטרקציות פנימיות — חיפוש, תמחור, הוספה לחבילה דינמית או להזמנת מלון.
 * מצב עמוד תוצאות: context="search" + targetType="destination" + browseMode + destinationId (ללא targetId).
 */
export default function AttractionsUpsellBlock({
  context,
  targetType,
  targetId = null,
  destinationId = null,
  category = "",
  visitDate = null,
  dateFrom = null,
  dateTo = null,
  partySize = 1,
  title = "חוויות והמלצות באזור",
  currencyLabel = "₪",
  packageRulesByAttractionId = null,
  hideBlockTitle = false,
  suppressEmptyDisplay = false,
  browseMode = false,
  /** פרופיל מלון: צפייה בלבד — ללא הוראות הוספה להזמנה (מניעת בלבול מול מסלול order) */
  hotelProfileBrowseOnly = false,
  fetchLimit = 24,
  previewMax = null,
  hideOnError = false,
  debugLabel = "",
  onUpsellStateChange = null,
  /** כאשר true — לא לקרוא ל־searchAttractions; להציג רשימה שכבר נטענה ב־SearchContext (חיפוש מקביל). */
  useExternalAttractionsOnly = false,
  externalAttractions = null,
  /** מזהי אטרקציות שכבר מצורפות לחבילה (למשל מ־package-lines) */
  attachedAttractionIds = null,
  /** attraction_id → שורת package_attractions (למחיר מצורף) */
  packageAttachmentsByAttractionId = null,
  /** אחרי הוספה מוצלחת — רענון שורות חבילה מבחוץ */
  onAfterOrderMutation = null,
  addButtonLabel = "הוספה להזמנה",
  includedInPackageLabel = "כלול בחבילה",
  filterAttractions = null,
  emptyMessage = null,
}) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [items, setItems] = useState([]);
  const [prices, setPrices] = useState({});
  const [addingId, setAddingId] = useState(null);
  const [previewExpanded, setPreviewExpanded] = useState(false);
  const [hiddenByError, setHiddenByError] = useState(false);

  const isDestinationBrowse =
    browseMode || (context === "search" && targetType === "destination");
  const forSearchBoth = context === "search" && targetType === "destination";

  const forPackages = context === "package";
  const forHotel = context === "hotel";

  const effectiveVisit = visitDate || dateFrom || null;

  const searchGateOk = useMemo(() => {
    if (isDestinationBrowse) {
      const d = destinationId != null ? Number(destinationId) : NaN;
      return Number.isFinite(d) && d > 0;
    }
    const t = targetId != null ? Number(targetId) : NaN;
    return Number.isFinite(t);
  }, [isDestinationBrowse, destinationId, targetId]);

  const upgradeItems = useMemo(() => {
    if (!forPackages || !packageRulesByAttractionId || packageRulesByAttractionId.size === 0) {
      return items;
    }
    return items.filter((a) =>
      attractionShowsInBillableUpsell(a?.id, packageRulesByAttractionId)
    );
  }, [items, forPackages, packageRulesByAttractionId]);

  const visibleUpgradeItems = useMemo(() => {
    if (
      previewMax == null ||
      previewMax <= 0 ||
      previewExpanded ||
      upgradeItems.length <= previewMax
    ) {
      return upgradeItems;
    }
    return upgradeItems.slice(0, previewMax);
  }, [upgradeItems, previewMax, previewExpanded]);

  const loadSearch = useCallback(async () => {
    if (useExternalAttractionsOnly) {
      if (!searchGateOk) {
        setItems([]);
        setLoading(false);
        return;
      }
      const rawItems = Array.isArray(externalAttractions) ? externalAttractions : [];
      setItems(typeof filterAttractions === "function" ? filterAttractions(rawItems) : rawItems);
      setLoading(false);
      setError(null);
      setHiddenByError(false);
      return;
    }
    if (!searchGateOk) {
      setItems([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    setHiddenByError(false);
    try {
      const lim = Math.max(1, Math.min(Number(fetchLimit) || 24, 200));
      const res = await searchAttractions({
        destination_id: destinationId ?? undefined,
        date_from: dateFrom || undefined,
        date_to: dateTo || dateFrom || undefined,
        category: category || undefined,
        limit: lim,
        for_packages: forSearchBoth ? true : forPackages ? true : undefined,
        for_hotel_upsell: forSearchBoth ? true : forHotel ? true : undefined,
      });
      const rawItems = Array.isArray(res?.attractions) ? res.attractions : [];
      setItems(typeof filterAttractions === "function" ? filterAttractions(rawItems) : rawItems);
    } catch (e) {
      if (hideOnError) {
        if (import.meta.env.DEV && debugLabel) {
          console.warn("[AttractionsUpsellBlock]", debugLabel, e);
        }
        setHiddenByError(true);
        setError(null);
      } else {
        const msg =
          e?.response?.data?.message ||
          e?.response?.data?.error ||
          e?.message ||
          "לא ניתן לטעון אטרקציות";
        setError(String(msg));
      }
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [
    useExternalAttractionsOnly,
    externalAttractions,
    searchGateOk,
    destinationId,
    category,
    dateFrom,
    dateTo,
    forPackages,
    forHotel,
    forSearchBoth,
    fetchLimit,
    hideOnError,
    debugLabel,
    filterAttractions,
  ]);

  useEffect(() => {
    setPreviewExpanded(false);
  }, [destinationId, searchGateOk, context, targetType]);

  useEffect(() => {
    loadSearch();
  }, [loadSearch]);

  useEffect(() => {
    let cancelled = false;
    async function run() {
      if (isDestinationBrowse) {
        setPrices({});
        return;
      }
      if (!effectiveVisit || !upgradeItems.length) {
        setPrices({});
        return;
      }
      const next = {};
      await Promise.all(
        upgradeItems.map(async (a) => {
          const id = a?.id;
          if (id == null) return;
          const rule =
            forPackages && packageRulesByAttractionId
              ? packageRulesByAttractionId.get(Number(id))
              : null;
          const isGift =
            rule && String(rule.display_mode || "").toLowerCase() === "gift";
          if (isGift) {
            return;
          }
          try {
            const r = await calculateAttractionPrice({
              attraction_id: Number(id),
              visit_date: effectiveVisit,
              party_size: partySize,
            });
            if (!cancelled && r?.price) next[id] = r.price;
          } catch (_) {
            if (!cancelled) next[id] = null;
          }
        })
      );
      if (!cancelled) setPrices(next);
    }
    run();
    return () => {
      cancelled = true;
    };
  }, [
    isDestinationBrowse,
    upgradeItems,
    effectiveVisit,
    partySize,
    forPackages,
    packageRulesByAttractionId,
  ]);

  const hint = useMemo(() => {
    if (isDestinationBrowse) return null;
    if (!items.length && !loading) return null;
    if (
      forPackages &&
      packageRulesByAttractionId &&
      packageRulesByAttractionId.size > 0 &&
      items.length > 0 &&
      upgradeItems.length === 0
    ) {
      return "אין שדרוגים בתשלום נוספים להצגה — ההטבות והמתנות מופיעות למעלה.";
    }
    if (!effectiveVisit) return "בחרו תאריך ביקור כדי לראות מחיר מדויק.";
    return null;
  }, [
    isDestinationBrowse,
    items.length,
    loading,
    effectiveVisit,
    forPackages,
    packageRulesByAttractionId,
    upgradeItems.length,
  ]);

  useEffect(() => {
    if (!onUpsellStateChange) return;
    onUpsellStateChange({
      loading,
      upgradeItemsLength: upgradeItems.length,
      itemsLength: items.length,
      error: error ? String(error) : null,
    });
  }, [loading, upgradeItems.length, items.length, error, onUpsellStateChange]);

  const isAttractionAttached = (id) => {
    if (id == null) return false;
    const setOrMap = attachedAttractionIds;
    if (setOrMap instanceof Set) return setOrMap.has(Number(id));
    if (Array.isArray(setOrMap)) return setOrMap.map(Number).includes(Number(id));
    return false;
  };

  const handleAdd = async (attractionId) => {
    if (isDestinationBrowse) return;
    if (targetId == null || Number.isNaN(Number(targetId))) return;
    setAddingId(attractionId);
    setError(null);
    try {
      await addAttractionToOrder({
        target: targetType,
        target_id: Number(targetId),
        attraction_id: Number(attractionId),
        visit_date: effectiveVisit || undefined,
        party_size: partySize,
      });
      message.success("נוסף להזמנה");
      await loadSearch();
      if (typeof onAfterOrderMutation === "function") {
        await onAfterOrderMutation();
      }
    } catch (e) {
      const msg =
        e?.response?.data?.message ||
        e?.response?.data?.error ||
        e?.message ||
        "ההוספה נכשלה";
      setError(String(msg));
    } finally {
      setAddingId(null);
    }
  };

  if (hiddenByError) return null;

  if (!searchGateOk) return null;

  if (
    suppressEmptyDisplay &&
    !loading &&
    upgradeItems.length === 0 &&
    !error
  ) {
    return null;
  }

  const emptyHint =
    emptyMessage ||
    (isDestinationBrowse
      ? "אין חוויות להצגה ליעד הזה כרגע."
      : "אין אטרקציות זמינות להצגה.");

  return (
    <section
      className={`attractions-upsell${isDestinationBrowse ? " attractions-upsell--browse" : ""}`}
      aria-label={title}
    >
      {hideBlockTitle ? null : <h3 className="attractions-upsell__title">{title}</h3>}
      {hint ? <p className="attractions-upsell__hint">{hint}</p> : null}
      {error ? <p className="attractions-upsell__error">{error}</p> : null}
      {loading ? (
        isDestinationBrowse ? (
          <div className="attractions-upsell__skeleton">
            <Skeleton active paragraph={{ rows: 1 }} />
            <Skeleton active paragraph={{ rows: 1 }} />
          </div>
        ) : (
          <Spin />
        )
      ) : items.length === 0 ? (
        suppressEmptyDisplay ? null : (
          <p className="attractions-upsell__hint attractions-upsell__hint--muted">{emptyHint}</p>
        )
      ) : upgradeItems.length === 0 ? (
        suppressEmptyDisplay ? null : (
          <p className="attractions-upsell__hint">
            אין שדרוגים בתשלום להצגה באזור זה (ההטבות והמתנות מופיעות בבלוק למעלה).
          </p>
        )
      ) : (
        <>
          <div className="attractions-upsell__grid hr-cards-row hr-cards-row--grid">
            {visibleUpgradeItems.map((a) => {
              const id = a.id;
              const rule =
                forPackages && packageRulesByAttractionId
                  ? packageRulesByAttractionId.get(Number(id))
                  : null;
              const isGift =
                rule && String(rule.display_mode || "").toLowerCase() === "gift";
              const autoAttach = rule?.auto_attach === true || rule?.auto_attach === "true";
              const attached = isAttractionAttached(id);
              const attachedRow =
                packageAttachmentsByAttractionId &&
                typeof packageAttachmentsByAttractionId === "object"
                  ? packageAttachmentsByAttractionId[Number(id)]
                  : null;
              const attachedGross =
                attachedRow != null ? Number(attachedRow.total_gross) : NaN;
              const dur =
                a.duration_minutes != null
                  ? `${a.duration_minutes} דק׳`
                  : a.category
                    ? a.category
                    : "";
              const p = prices[id];
              const priceLabel = isGift
                ? ""
                : attached && Number.isFinite(attachedGross) && attachedGross > 0
                  ? `${currencyLabel}${Math.round(attachedGross).toLocaleString()}`
                  : p != null && p.total_gross != null
                    ? `${currencyLabel}${Number(p.total_gross).toFixed(0)}`
                    : effectiveVisit
                      ? "—"
                      : a.base_price != null
                        ? `${currencyLabel}${Number(a.base_price).toFixed(0)}+`
                        : "—";
              const statusLabel = (() => {
                if (isGift) return "מתנה";
                if (attached) return "בתשלום · בחבילה";
                return "בתשלום";
              })();
              return (
                <article key={id} className="attractions-upsell__card hr-card-layout">
                  <div className="attractions-upsell__img-wrap hr-card-layout__header">
                    {a.image_url ? (
                      <img className="attractions-upsell__img" src={a.image_url} alt="" loading="lazy" />
                    ) : null}
                  </div>
                  <div className="attractions-upsell__body hr-card-layout__content">
                    <h4 className="attractions-upsell__name hr-text-clamp-2">{a.name}</h4>
                    {!isDestinationBrowse ? (
                      <span
                        className={`attractions-upsell__status-pill${attached ? " attractions-upsell__status-pill--attached" : ""}`}
                      >
                        {statusLabel}
                      </span>
                    ) : null}
                    {a.description_short ? (
                      <p className="attractions-upsell__meta attractions-upsell__desc">
                        {a.description_short}
                      </p>
                    ) : null}
                    {dur ? (
                      <p className="attractions-upsell__meta hr-text-clamp-2">{dur}</p>
                    ) : null}
                    {!isDestinationBrowse && !isGift ? (
                      <div className="attractions-upsell__price">{priceLabel}</div>
                    ) : null}
                  </div>
                  <div className="attractions-upsell__actions hr-card-layout__footer">
                    {isDestinationBrowse ? (
                      <span className="attractions-upsell__browse-note">
                        {hotelProfileBrowseOnly
                          ? "זמין ביעד · צפייה בלבד"
                          : "הוספה — מדף המלון או ההזמנה"}
                      </span>
                    ) : isGift && autoAttach ? (
                      <span className="attractions-upsell__included-label" aria-label="כלול בהזמנה">
                        כלול בהזמנה
                      </span>
                    ) : attached ? (
                      <Button type="default" size="small" disabled>
                        {includedInPackageLabel}
                      </Button>
                    ) : (
                      <Button
                        type="primary"
                        size="small"
                        loading={addingId === id}
                        disabled={!effectiveVisit}
                        onClick={() => handleAdd(id)}
                      >
                        {addButtonLabel}
                      </Button>
                    )}
                  </div>
                </article>
              );
            })}
          </div>
          {previewMax != null &&
          previewMax > 0 &&
          upgradeItems.length > previewMax &&
          !previewExpanded ? (
            <div className="attractions-upsell__more-wrap">
              <Button type="default" size="small" onClick={() => setPreviewExpanded(true)}>
                הצג עוד ({upgradeItems.length - previewMax})
              </Button>
            </div>
          ) : null}
        </>
      )}
    </section>
  );
}

export { ldsYyyymmddToIso, countPaxFromRooms };
